import React, { useState, useContext, useEffect } from 'react';
import AuthContext from '../context/AuthContext';
import WalkerMap from './WalkerMap';

const FindWalker = () => {
  const { getToken } = useContext(AuthContext);
  const [location, setLocation] = useState(null);
  const [radius, setRadius] = useState(2);
  const [walkers, setWalkers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [dogs, setDogs] = useState([]);
  const [selectedDogId, setSelectedDogId] = useState('');
  const [bookingForm, setBookingForm] = useState(null);

  useEffect(() => {
    const loadDogs = async () => {
      try {
        const response = await fetch('/api/dogs', {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        const dogData = await response.json();
        setDogs(dogData);
        if (dogData.length === 1) setSelectedDogId(dogData[0].id);
      } catch (err) {
        // Dog list is optional at search time; search still works without it.
      }
    };
    loadDogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }
    setError('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (geoError) => {
        const messages = {
          1: 'Location permission was denied. Allow location access in your browser settings and try again.',
          2: 'Your location is currently unavailable. Check your device\'s location/GPS settings.',
          3: 'Getting your location timed out. Please try again.'
        };
        setError(messages[geoError.code] || `Could not get your location: ${geoError.message}`);
      },
      { timeout: 10000 }
    );
  };

  const searchWalkers = async () => {
    if (!location) {
      setError('Please enable location first');
      return;
    }

    setSearching(true);
    try {
      const walkerResponse = await fetch('/api/search/walkers', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          latitude: location.latitude,
          longitude: location.longitude,
          radius_miles: parseInt(radius),
          dog_id: selectedDogId || undefined
        })
      });

      const data = await walkerResponse.json();
      if (!walkerResponse.ok) throw new Error(data.error);

      setWalkers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  // Walker rates are quoted per 30-minute walk; price scales with duration.
  const priceForDuration = (ratePerWalk, minutes) => {
    const rate = Number(ratePerWalk) || 12;
    return parseFloat(((rate * minutes) / 30).toFixed(2));
  };

  const openBookingForm = (walker) => {
    const duration = 30;
    setBookingForm({
      walker_id: walker.user_id,
      walker_name: `${walker.first_name} ${walker.last_name}`,
      rate_per_walk_gbp: walker.rate_per_walk_gbp,
      dog_id: selectedDogId || '',
      scheduled_duration_minutes: duration,
      price_gbp: priceForDuration(walker.rate_per_walk_gbp, duration)
    });
  };

  const updateDuration = (minutes) => {
    setBookingForm(prev => ({
      ...prev,
      scheduled_duration_minutes: minutes,
      price_gbp: priceForDuration(prev.rate_per_walk_gbp, minutes)
    }));
  };

  const submitBooking = async () => {
    if (!bookingForm.dog_id) {
      setError('Please select a dog');
      return;
    }

    try {
      const response = await fetch('/api/bookings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          walker_id: bookingForm.walker_id,
          dog_id: bookingForm.dog_id,
          scheduled_start: new Date().toISOString(),
          scheduled_duration_minutes: parseInt(bookingForm.scheduled_duration_minutes),
          price_gbp: parseFloat(bookingForm.price_gbp)
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      alert(`Booking request sent to ${bookingForm.walker_name}!`);
      setBookingForm(null);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="search-panel">
      <h2>Find Nearby Walkers</h2>
      {error && <p className="error">{error}</p>}

      <div className="search-controls">
        <button onClick={getLocation}>📍 Get My Location</button>
        {location && (
          <p>Location: {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}</p>
        )}

        <div className="radius-control">
          <label>Search Radius:</label>
          <select value={radius} onChange={(e) => setRadius(e.target.value)}>
            <option value="0.5">0.5 miles</option>
            <option value="1">1 mile</option>
            <option value="2">2 miles</option>
            <option value="5">5 miles</option>
          </select>
        </div>

        {dogs.length > 0 && (
          <div className="dog-control">
            <label>Walking:</label>
            <select value={selectedDogId} onChange={(e) => setSelectedDogId(e.target.value)}>
              <option value="">Any dog</option>
              {dogs.map(dog => (
                <option key={dog.id} value={dog.id}>{dog.name}</option>
              ))}
            </select>
          </div>
        )}

        <button onClick={searchWalkers} disabled={searching || !location}>
          {searching ? 'Searching...' : 'Search Walkers'}
        </button>
      </div>

      {bookingForm && (
        <div className="booking-modal">
          <div className="booking-form">
            <h3>Book {bookingForm.walker_name}</h3>
            <label>
              Select Dog:
              <select 
                value={bookingForm.dog_id} 
                onChange={(e) => setBookingForm({...bookingForm, dog_id: e.target.value})}
              >
                <option value="">-- Choose a dog --</option>
                {dogs.map(dog => (
                  <option key={dog.id} value={dog.id}>{dog.name}</option>
                ))}
              </select>
            </label>
            <label>
              Duration (minutes):
              <select
                value={bookingForm.scheduled_duration_minutes}
                onChange={(e) => updateDuration(parseInt(e.target.value))}
              >
                <option value="30">30 minutes</option>
                <option value="60">60 minutes</option>
                <option value="90">90 minutes</option>
              </select>
            </label>
            <p className="booking-price">Price: £{bookingForm.price_gbp.toFixed ? bookingForm.price_gbp.toFixed(2) : bookingForm.price_gbp}</p>
            <div className="booking-buttons">
              <button onClick={submitBooking} className="confirm">Confirm Booking</button>
              <button onClick={() => setBookingForm(null)} className="cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {walkers.length > 0 && (
        <WalkerMap ownerLocation={location} walkers={walkers} onSelectWalker={openBookingForm} />
      )}

      {walkers.length > 0 && (
        <div className="walkers-list">
          <h3>Available Walkers ({walkers.length})</h3>
          {walkers.map((walker) => (
            <div key={walker.id} className={`walker-card${walker.good_match ? ' good-match' : ''}`}>
              <div className="walker-card-header">
                <div className="walker-avatar">
                  {walker.photo_url
                    ? <img src={walker.photo_url} alt={`${walker.first_name} ${walker.last_name}`} />
                    : <span>{walker.first_name?.[0]}{walker.last_name?.[0]}</span>}
                </div>
                <div>
                  <h4>{walker.first_name} {walker.last_name}</h4>
                  <p className="walker-distance">📍 {walker.distance_miles} miles away</p>
                </div>
              </div>
              {walker.good_match === true && (
                <p className="match-badge">✓ Good match for {dogs.find(d => d.id === selectedDogId)?.name || 'your dog'}</p>
              )}
              <p>⭐ {walker.average_rating || 'No ratings yet'} · ✓ {walker.completed_walks} walks completed</p>
              <p>{walker.verification_status === 'verified' ? '✓ Verified' : 'Not yet verified'}</p>
              <p>{walker.available_now ? 'Available now' : 'Not currently available'}</p>
              <p className="walker-price">£{Number(walker.rate_per_walk_gbp || 12).toFixed(2)} / 30 min walk</p>
              <button onClick={() => openBookingForm(walker)}>Book / Request Walk</button>
            </div>
          ))}
        </div>
      )}

      {searching === false && walkers.length === 0 && location && (
        <p>No walkers found in your area. Try increasing the search radius.</p>
      )}
    </div>
  );
};

export default FindWalker;
